import { useEffect, useRef } from 'react';

// Leaving the app for an external page (Stripe's portal/checkout) and pressing
// back restores the page from the browser's bfcache with React state intact, so
// any "redirecting…" flag set just before the jump is still true and the button
// stays stuck. pageshow fires on that restore (persisted === true) and lets us
// clear it. visibilitychange covers the browsers that skip bfcache but keep the
// tab alive.
//
// The visibilitychange half fires on every alt-tab, and callers hang a refetch
// off this, so it is gated on the tab having actually been away long enough for
// a trip to Stripe. A glance at another window is not a return from checkout.
const MIN_HIDDEN_MS = 1500;

export default function useResetOnRestore(reset) {
  // Held in a ref so an inline callback from the caller doesn't tear down and
  // re-add the listeners on every render.
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    let hiddenAt = null;

    const onPageShow = (e) => {
      if (e.persisted) resetRef.current();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt === null) return;
      const away = Date.now() - hiddenAt;
      hiddenAt = null;
      if (away >= MIN_HIDDEN_MS) resetRef.current();
    };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
